/**
 * Turns whatever someone pastes from an AI tool into separate HTML, CSS and
 * JS: a whole chat reply with ```html/```css/```js blocks, a complete HTML
 * page, or a bare fragment. Works on the text; never runs anything.
 */

export type PastedCode = {
  html: string;
  css: string;
  js: string;
  /** How the code was found. */
  source: 'blocks' | 'document' | 'fragment' | 'empty';
  /** <script src> and non-font <link rel=stylesheet> the code expected. */
  externalScripts: string[];
  externalStyles: string[];
  /** Google Fonts stylesheets turned into @import. */
  fontImports: string[];
  /** Things removed from the page wrapper, for the report. */
  removed: {
    pageWrapper: boolean;
    styleBlocks: number;
    inlineScripts: number;
    moduleScripts: number;
    jsonScripts: number;
    tailwindConfig: boolean;
    bodyAttributes: string[];
    /** <script type="text/template"> and other scripts that aren't JavaScript. */
    otherScripts: number;
    /** Event handlers on <body> (onload…), moved into the script. */
    bodyHandlers: string[];
  };
  /** Code block languages that Genesis can't run as-is (scss, ts, jsx...). */
  unsupportedLanguages: string[];
  /** Blocks that aren't part of a section (shell commands, JSON, plain text), left out. */
  skippedLanguages: string[];
  /** Prose was present around the code (and ignored). */
  hadProse: boolean;
};

import { decodeEntities } from './inlineHandlers';

const HTML_LANGS = new Set(['html', 'htm', 'xhtml', 'xml', 'svg', 'markup', 'handlebars', 'hbs']);
const CSS_LANGS = new Set(['css']);
const JS_LANGS = new Set(['js', 'javascript', 'mjs', 'cjs', 'es6']);
const UNSUPPORTED = new Set(['scss', 'sass', 'less', 'stylus', 'ts', 'typescript', 'jsx', 'tsx', 'vue', 'svelte']);
// Replies often include install commands, config or sample data: not part of the section,
// and appended to the script they'd stop it with a syntax error.
const NOT_CODE = new Set([
  'json', 'jsonc', 'json5', 'sh', 'bash', 'shell', 'zsh', 'console', 'terminal', 'powershell', 'ps1', 'cmd', 'bat',
  'text', 'txt', 'plaintext', 'plain', 'md', 'markdown', 'diff', 'yaml', 'yml', 'toml', 'ini', 'env', 'dotenv',
  'http', 'sql', 'python', 'py', 'ruby', 'rb', 'go', 'java', 'php', 'csv', 'log'
]);
// <script type> values browsers run as JavaScript (an empty type too).
const JS_SCRIPT_TYPES = new Set(['', 'text/javascript', 'application/javascript', 'module', 'text/ecmascript', 'application/ecmascript']);
const FONT_HOSTS = /^https?:\/\/fonts\.(googleapis|bunny)\.net\/|^https?:\/\/fonts\.googleapis\.com\//i;

const getAttr = (attrs: string, name: string): string | null => {
  const m = attrs.match(new RegExp(`(?:^|\\s)${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`, 'i'));
  if (!m) return null;
  return m[1] ?? m[2] ?? m[3] ?? '';
};

/** Guesses the language of an unlabelled code block. */
export const sniffLanguage = (code: string): 'html' | 'css' | 'js' => {
  const t = code.trim();
  if (/^</.test(t)) return 'html';
  // CSS: starts with a selector or at-rule followed by a block, and has no JS keywords at line starts.
  const looksCss = /^(@[a-z-]+[^{;]*|[.#:*a-zA-Z[][^{;]*)\{[^}]*:[^}]*\}/.test(t)
    && !/^\s*(const|let|var|function|import|export|document|window|if|for|class)\b/m.test(t);
  return looksCss ? 'css' : 'js';
};

type Block = { lang: string; code: string };

const findBlocks = (text: string): { blocks: Block[]; prose: string } => {
  const blocks: Block[] = [];
  // The body is optional, and tried as empty first (??): an empty block (```js then ```) has no line
  // between its fences, and otherwise the match runs on to the next block's closing fence and swallows it.
  const re = /(^|\n)[ \t]*(```|~~~)[ \t]*([\w+#.-]*)[^\n]*\n(?:([\s\S]*?)\n)??[ \t]*\2[ \t]*(?=\n|$)/g;
  let prose = '';
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    prose += text.slice(last, m.index);
    blocks.push({ lang: m[3].toLowerCase(), code: m[4] ?? '' });
    last = re.lastIndex;
  }
  prose += text.slice(last);
  // A reply copied only partway can end inside a block: take the rest as that block.
  const open = prose.match(/(^|\n)[ \t]*(```|~~~)[ \t]*([\w+#.-]*)[^\n]*\n([\s\S]*)$/);
  if (open && open[4].trim()) {
    blocks.push({ lang: open[3].toLowerCase(), code: open[4] });
    prose = prose.slice(0, open.index);
  }
  return { blocks, prose };
};

type HtmlParts = Pick<PastedCode, 'externalScripts' | 'externalStyles' | 'fontImports' | 'removed'> & {
  html: string; css: string[]; js: string[]; wasDocument: boolean;
};

/** Pulls <style>, <script> and <link> out of HTML and removes the page wrapper. */
export const splitHtmlDocument = (input: string): HtmlParts => {
  const parts: HtmlParts = {
    html: input,
    css: [],
    js: [],
    wasDocument: false,
    externalScripts: [],
    externalStyles: [],
    fontImports: [],
    removed: { pageWrapper: false, styleBlocks: 0, inlineScripts: 0, moduleScripts: 0, jsonScripts: 0, tailwindConfig: false, bodyAttributes: [], otherScripts: 0, bodyHandlers: [] }
  };
  let html = input;

  html = html.replace(/<script\b([^>]*)>([\s\S]*?)<\/script\s*>/gi, (_m, attrs: string, body: string) => {
    const src = getAttr(attrs, 'src');
    const type = (getAttr(attrs, 'type') || '').toLowerCase();
    if (src) { parts.externalScripts.push(src); return ''; }
    if (type.includes('json')) { parts.removed.jsonScripts++; return ''; }
    if (!JS_SCRIPT_TYPES.has(type)) { parts.removed.otherScripts++; return ''; }
    if (type === 'module') parts.removed.moduleScripts++;
    // Tailwind's CDN config only works with its script, which published pages don't load.
    if (/^\s*tailwind\.config\s*=/.test(body)) { parts.removed.tailwindConfig = true; return ''; }
    if (body.trim()) { parts.js.push(body.trim()); parts.removed.inlineScripts++; }
    return '';
  });

  html = html.replace(/<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi, (_m, body: string) => {
    if (body.trim()) parts.css.push(body.trim());
    parts.removed.styleBlocks++;
    return '';
  });

  html = html.replace(/<link\b([^>]*)\/?>/gi, (_m, attrs: string) => {
    const rel = (getAttr(attrs, 'rel') || '').toLowerCase();
    const href = getAttr(attrs, 'href') || '';
    if (rel.split(/\s+/).includes('stylesheet') && href) {
      if (FONT_HOSTS.test(href)) parts.fontImports.push(href);
      else parts.externalStyles.push(href);
    }
    return '';
  });

  const hasWrapper = /<!doctype\b|<html\b|<head\b|<body\b/i.test(html);
  if (hasWrapper) {
    parts.wasDocument = true;
    parts.removed.pageWrapper = true;
    const body = html.match(/<body\b([^>]*)>/i);
    if (body) {
      const attrs = body[1];
      ['class', 'style', 'id'].forEach((name) => {
        const value = getAttr(attrs, name);
        if (value) parts.removed.bodyAttributes.push(`${name}="${value}"`);
      });
      // <body onload="init()"> and friends listen on the window; keep them working from the script.
      const handlerRe = /(?:^|\s)on([a-z]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;
      let h: RegExpExecArray | null;
      while ((h = handlerRe.exec(attrs))) {
        const event = h[1].toLowerCase();
        const code = decodeEntities(h[2] ?? h[3] ?? '').trim();
        if (!code) continue; // eslint-disable-line no-continue
        parts.removed.bodyHandlers.push(`on${event}`);
        parts.js.push(`window.addEventListener(${JSON.stringify(event)}, function (event) {\n${code}\n});`);
      }
    }
    html = html
      .replace(/<!doctype[^>]*>/gi, '')
      .replace(/<head\b[^>]*>[\s\S]*?<\/head\s*>/gi, '')
      .replace(/<\/?(html|head|body)\b[^>]*>/gi, '')
      .replace(/<(meta|base)\b[^>]*>/gi, '')
      .replace(/<title\b[^>]*>[\s\S]*?<\/title\s*>/gi, '');
  }
  parts.html = html.replace(/^\s*\n/, '').trim();
  return parts;
};

export const parsePaste = (raw: string): PastedCode => {
  const text = (raw || '').replace(/\r\n?/g, '\n');
  const result: PastedCode = {
    html: '', css: '', js: '', source: 'empty',
    externalScripts: [], externalStyles: [], fontImports: [],
    removed: { pageWrapper: false, styleBlocks: 0, inlineScripts: 0, moduleScripts: 0, jsonScripts: 0, tailwindConfig: false, bodyAttributes: [], otherScripts: 0, bodyHandlers: [] },
    unsupportedLanguages: [],
    skippedLanguages: [],
    hadProse: false
  };
  if (!text.trim()) return result;

  const { blocks, prose } = findBlocks(text);
  const htmlChunks: string[] = [];
  const cssChunks: string[] = [];
  const jsChunks: string[] = [];

  if (blocks.length) {
    result.source = 'blocks';
    result.hadProse = /\S/.test(prose);
    blocks.forEach(({ lang, code }) => {
      if (NOT_CODE.has(lang)) {
        if (!result.skippedLanguages.includes(lang)) result.skippedLanguages.push(lang);
        return;
      }
      if (UNSUPPORTED.has(lang) && !result.unsupportedLanguages.includes(lang)) result.unsupportedLanguages.push(lang);
      let kind: 'html' | 'css' | 'js';
      if (HTML_LANGS.has(lang)) kind = 'html';
      else if (CSS_LANGS.has(lang) || ['scss', 'sass', 'less', 'stylus'].includes(lang)) kind = 'css';
      else if (JS_LANGS.has(lang) || ['ts', 'typescript', 'jsx', 'tsx'].includes(lang)) kind = 'js';
      else kind = sniffLanguage(code);
      if (kind === 'html') htmlChunks.push(code);
      else if (kind === 'css') cssChunks.push(code);
      else jsChunks.push(code);
    });
  } else {
    const kind = sniffLanguage(text);
    if (kind === 'html') htmlChunks.push(text);
    else if (kind === 'css') cssChunks.push(text);
    else jsChunks.push(text);
    result.source = 'fragment';
  }

  // Styles and scripts inside the HTML come first, in page order, then the separate blocks.
  const docCss: string[] = [];
  const docJs: string[] = [];
  const htmlOut: string[] = [];
  htmlChunks.forEach((chunk) => {
    const parts = splitHtmlDocument(chunk);
    if (parts.wasDocument) result.source = result.source === 'blocks' ? 'blocks' : 'document';
    htmlOut.push(parts.html);
    docCss.push(...parts.css);
    docJs.push(...parts.js);
    result.externalScripts.push(...parts.externalScripts);
    result.externalStyles.push(...parts.externalStyles);
    result.fontImports.push(...parts.fontImports);
    result.removed.pageWrapper = result.removed.pageWrapper || parts.removed.pageWrapper;
    result.removed.styleBlocks += parts.removed.styleBlocks;
    result.removed.inlineScripts += parts.removed.inlineScripts;
    result.removed.moduleScripts += parts.removed.moduleScripts;
    result.removed.jsonScripts += parts.removed.jsonScripts;
    result.removed.tailwindConfig = result.removed.tailwindConfig || parts.removed.tailwindConfig;
    result.removed.bodyAttributes.push(...parts.removed.bodyAttributes);
    result.removed.otherScripts += parts.removed.otherScripts;
    result.removed.bodyHandlers.push(...parts.removed.bodyHandlers);
  });

  const fontCss = result.fontImports.map(href => `@import url("${href}");`);
  result.html = htmlOut.filter(Boolean).join('\n\n');
  result.css = [...fontCss, ...docCss, ...cssChunks.map(c => c.trim())].filter(Boolean).join('\n\n');
  result.js = [...docJs, ...jsChunks.map(c => c.trim())].filter(Boolean).join('\n\n');
  return result;
};
